import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, Tab, Tabs } from "@mui/material";
import {
  Button,
  DialogComponentProps,
  Typography,
} from "@platform/design-system";
import { useState } from "react";
import { CENSUS_ELIMINATE_REDUNDANCY } from "@platform/feature-flags";

import CensusTab from "./CensusTab";
import ClassManagerTab from "./ClassManagerTab";

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({
  children,
  value,
  index,
}: TabPanelProps): React.JSX.Element {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        display: value === index ? "flex" : "none",
        flexDirection: "column",
      }}
    >
      {value === index && children}
    </Box>
  );
}

const BenefitsClassManagerDrawer = ({
  data,
  onClose,
}: DialogComponentProps): React.JSX.Element => {
  const rfpId = data?.rfpId as string | undefined;
  const ecoRfpId = data?.ecoRfpId as string | undefined;

  // Census tab state
  const [censusResetFormKey, setCensusResetFormKey] = useState(0);

  // Feature flag for Census tab
  const enableCensusEliminateRedundancy = useFeatureIsOn(
    CENSUS_ELIMINATE_REDUNDANCY,
  );

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    setActiveTab(newValue);
  };

  const handleResetCensusForm = (): void => {
    // Increment key to force re-run of useEffect in CensusTab
    setCensusResetFormKey((prev) => prev + 1);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header with title and Reset Form button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Typography variant="body-large" component="h2">
          {enableCensusEliminateRedundancy
            ? "Class Manager & Census"
            : "Class Manager"}
        </Typography>

        {/* Reset Form button - only visible on Census tab */}
        {activeTab === 1 && (
          <Button
            variant="tertiary"
            onClick={handleResetCensusForm}
            sx={{ textTransform: "uppercase" }}
          >
            Reset Form
          </Button>
        )}
      </Box>

      {/* Tabs - only show when feature flag is enabled */}
      {enableCensusEliminateRedundancy && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="CLASS MANAGER" />
            <Tab label="CENSUS" />
          </Tabs>
        </Box>
      )}

      {/* Class Manager Tab */}
      <TabPanel value={activeTab} index={0}>
        <ClassManagerTab ecoRfpId={ecoRfpId ?? ""} onClose={onClose} />
      </TabPanel>

      {/* Census Tab */}
      <TabPanel value={activeTab} index={1}>
        <CensusTab
          rfpId={rfpId}
          ecoRfpId={ecoRfpId}
          onClose={onClose}
          resetFormKey={censusResetFormKey}
        />
      </TabPanel>
    </Box>
  );
};

export default BenefitsClassManagerDrawer;
